import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDuration } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getUnit } from "@/lib/weight";
import WeightInput, { fromCanonicalLb, toCanonicalLb, formatStoredWeight } from "@/components/app/WeightInput";
import UnitInput from "@/components/app/UnitInput";
import TimeInput from "@/components/app/TimeInput";
import {
  toCanonicalMeters, fromCanonicalMeters, formatStoredDistance,
  toCanonicalInches, fromCanonicalInches, formatStoredHeight,
} from "@/lib/units";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { BookOpen, Plus, PencilSimple, Trash, Barbell, VideoCamera, Shield, MagnifyingGlass, Fire, Lightning, Timer, Barbell as BarbellIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AnatomyFields, BodyPartFilterPills, BodyPartChip } from "@/components/app/AnatomyFields";

function ExerciseForm({ initial, onSave, onCancel }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  const [m, setM] = useState(() => {
    const base = initial || {
      name: "", sets: 3, reps: null, weight: null, weight_unit: null,
      time_seconds: null, distance_meters: null, distance_unit: null,
      height_inches: null, height_unit: null,
      calories: null, rpe: null,
      notes: "", video_url: "", form_notes: "",
      body_part: null, primary_muscle: null,
    };
    const wUnit = base.weight_unit || userUnit;
    const dUnit = base.distance_unit || "m";
    const hUnit = base.height_unit || "in";
    return {
      ...base,
      weight: fromCanonicalLb(base.weight, wUnit),
      weight_unit: wUnit,
      distance: fromCanonicalMeters(base.distance_meters, dUnit),
      distance_unit: dUnit,
      height: fromCanonicalInches(base.height_inches, hUnit),
      height_unit: hUnit,
    };
  });
  const update = (k, v) => setM((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          name: m.name.trim(),
          sets: Number(m.sets) || 1,
          reps: num(m.reps),
          weight: toCanonicalLb(m.weight, m.weight_unit),
          weight_unit: m.weight_unit || "lb",
          time_seconds: num(m.time_seconds),
          distance_meters: toCanonicalMeters(m.distance, m.distance_unit),
          distance_unit: m.distance_unit || "m",
          height_inches: toCanonicalInches(m.height, m.height_unit),
          height_unit: m.height_unit || "in",
          calories: num(m.calories),
          rpe: num(m.rpe),
          notes: m.notes || null,
          video_url: (m.video_url || "").trim() || null,
          form_notes: m.form_notes || null,
          body_part: m.body_part || null,
          primary_muscle: m.primary_muscle || null,
        });
      }}
      className="space-y-4"
      data-testid="exercise-form"
    >
      <div>
        <Label className="hs-label">Exercise name</Label>
        <Input required value={m.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-11 rounded-xl" data-testid="exercise-name" placeholder="Goblet Squat" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="hs-label">Sets</Label><Input type="number" min="1" value={m.sets ?? ""} onChange={(e) => update("sets", e.target.value)} className="mt-2 h-11 rounded-xl font-data" /></div>
        <div><Label className="hs-label">Reps</Label><Input type="number" min="0" value={m.reps ?? ""} onChange={(e) => update("reps", e.target.value)} className="mt-2 h-11 rounded-xl font-data" /></div>
        <WeightInput
          value={m.weight}
          unit={m.weight_unit}
          onChange={(v, u) => setM({ ...m, weight: v, weight_unit: u })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><TimeInput valueSeconds={m.time_seconds ?? ""} onChangeSeconds={(v) => update("time_seconds", v)} testId="exercise-time" /></div>
        <div><Label className="hs-label">Calories</Label><Input type="number" min="0" value={m.calories ?? ""} onChange={(e) => update("calories", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="exercise-calories" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <UnitInput kind="distance" value={m.distance} unit={m.distance_unit} onChange={(v, u) => setM({ ...m, distance: v, distance_unit: u })} />
        <UnitInput kind="height" value={m.height} unit={m.height_unit} onChange={(v, u) => setM({ ...m, height: v, height_unit: u })} />
      </div>
      <div>
        <Label className="hs-label">RPE</Label>
        <Input type="number" step="0.5" min="0" max="10" value={m.rpe ?? ""} onChange={(e) => update("rpe", e.target.value)} className="mt-2 h-11 rounded-xl font-data" />
      </div>
      <AnatomyFields
        bodyPart={m.body_part}
        primaryMuscle={m.primary_muscle}
        onChange={({ body_part, primary_muscle }) => setM({ ...m, body_part, primary_muscle })}
      />
      <div>
        <Label className="hs-label flex items-center gap-1.5"><VideoCamera size={12} /> Demo video URL</Label>
        <Input type="url" value={m.video_url ?? ""} onChange={(e) => update("video_url", e.target.value)} className="mt-2 h-11 rounded-xl font-data text-sm" placeholder="https://youtube.com/…" />
      </div>
      <div>
        <Label className="hs-label flex items-center gap-1.5"><Shield size={12} /> Form-check notes</Label>
        <Textarea rows={3} value={m.form_notes ?? ""} onChange={(e) => update("form_notes", e.target.value)} className="mt-2 rounded-xl" placeholder="Knees track over toes. Neutral spine…" />
      </div>
      <div>
        <Label className="hs-label">Coach notes</Label>
        <Textarea rows={2} value={m.notes ?? ""} onChange={(e) => update("notes", e.target.value)} className="mt-2 rounded-xl" placeholder="Tempo, equipment…" />
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-xl">Cancel</Button>
        <Button type="submit" className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="save-exercise-button">Save</Button>
      </DialogFooter>
    </form>
  );
}

function ExerciseCard({ ex, onEdited, onDeleted }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  const exUnit = ex.weight_unit || userUnit;
  const [editOpen, setEditOpen] = useState(false);
  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/exercises/${ex.id}`, patch);
      toast.success("Updated");
      setEditOpen(false);
      onEdited(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${ex.name}" from library?`)) return;
    try {
      await api.delete(`/exercises/${ex.id}`);
      toast.success("Removed from library");
      onDeleted(ex.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="hs-card p-5 animate-fade-up" data-testid={`exercise-card-${ex.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Barbell size={18} className="text-moss shrink-0" />
          <h3 className="font-heading text-lg font-semibold truncate">{ex.name}</h3>
        </div>
        <div className="flex gap-1 shrink-0">
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button size="icon" variant="ghost" className="rounded-lg hover:bg-muted2" data-testid={`edit-exercise-${ex.id}`}>
                <PencilSimple size={16} />
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Edit exercise</DialogTitle>
                <DialogDescription>Update the saved template for {ex.name}.</DialogDescription>
              </DialogHeader>
              <ExerciseForm initial={ex} onSave={save} onCancel={() => setEditOpen(false)} />
            </DialogContent>
          </Dialog>
          <Button size="icon" variant="ghost" onClick={del} className="rounded-lg text-error hover:bg-red-50 hover:text-error" data-testid={`delete-exercise-${ex.id}`}>
            <Trash size={16} />
          </Button>
        </div>
      </div>
      {(ex.body_part || ex.primary_muscle) && (
        <div className="mt-2">
          <BodyPartChip bodyPart={ex.body_part} primaryMuscle={ex.primary_muscle} />
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2 font-data text-sm">
        {ex.sets ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{ex.sets} sets</span> : null}
        {ex.reps ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{ex.reps} reps</span> : null}
        {ex.weight ? <span className="px-2.5 py-1 rounded-lg bg-sage text-moss font-semibold">@ {formatStoredWeight(ex.weight, exUnit)}</span> : null}
        {ex.time_seconds ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{formatDuration(ex.time_seconds)}</span> : null}
        {ex.distance_meters ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{formatStoredDistance(ex.distance_meters, ex.distance_unit || "m")}</span> : null}
        {ex.height_inches ? <span className="px-2.5 py-1 rounded-lg bg-muted2">↕ {formatStoredHeight(ex.height_inches, ex.height_unit || "in")}</span> : null}
        {ex.calories ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold inline-flex items-center gap-1"><Fire size={12} weight="fill" /> {ex.calories} cal</span> : null}
      </div>
      {ex.form_notes && (
        <div className="mt-3 p-3 rounded-xl bg-sage/60 border border-sage">
          <div className="flex items-center gap-1.5 hs-label text-[10px] text-moss">
            <Shield size={12} weight="bold" /> Form check
          </div>
          <p className="mt-1 text-sm text-moss-hover leading-relaxed">{ex.form_notes}</p>
        </div>
      )}
      {ex.video_url && (
        <a href={ex.video_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-terracotta hover:text-terracotta-hover">
          <VideoCamera size={13} /> Demo video
        </a>
      )}
    </div>
  );
}

export default function Library() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [bodyPart, setBodyPart] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/exercises");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (body) => {
    try {
      const { data } = await api.post("/exercises", body);
      setItems((xs) => [...xs, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`${body.name} added to library`);
      setAddOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter((e) => {
    if (bodyPart && (e.body_part || "") !== bodyPart) return false;
    if (q && !e.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const bodyPartCounts = items.reduce((acc, e) => {
    const k = e.body_part || "__unset";
    if (k !== "__unset") acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="hs-label">Exercise library</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 flex items-center gap-3">
            <BookOpen size={28} className="text-terracotta" weight="fill" /> Your saved exercises
          </h1>
          <p className="text-ink-2 mt-1 text-sm">Save a movement once; reuse it across every athlete's weekly program with a single tap.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="add-exercise-button">
              <Plus size={16} className="mr-2" /> New exercise
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">New exercise</DialogTitle>
              <DialogDescription>Save a reusable template with default sets, reps, weight, calories, form cues and demo video.</DialogDescription>
            </DialogHeader>
            <ExerciseForm onSave={create} onCancel={() => setAddOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="exercises" className="w-full">
        <TabsList className="bg-muted2 rounded-xl p-1">
          <TabsTrigger value="exercises" className="rounded-lg data-[state=active]:bg-surface" data-testid="lib-tab-exercises">
            <Barbell size={14} className="mr-1.5" /> Exercises
          </TabsTrigger>
          <TabsTrigger value="workouts" className="rounded-lg data-[state=active]:bg-surface" data-testid="lib-tab-workouts">
            <Lightning size={14} className="mr-1.5" /> Workouts
          </TabsTrigger>
          <TabsTrigger value="crossfit" className="rounded-lg data-[state=active]:bg-surface" data-testid="lib-tab-crossfit">
            <BarbellIcon size={14} weight="fill" className="mr-1.5" /> CrossFit movements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="exercises" className="mt-6 space-y-6">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-ink-2">Reusable single-movement templates with default sets, reps, video and form cues.</p>
            </div>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="add-exercise-button">
                  <Plus size={16} className="mr-2" /> New exercise
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">New exercise</DialogTitle>
                  <DialogDescription>Save a reusable template with default sets, reps, weight, calories, form cues and demo video.</DialogDescription>
                </DialogHeader>
                <ExerciseForm onSave={create} onCancel={() => setAddOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
            <Input placeholder="Search exercises…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-12 rounded-xl" data-testid="library-page-search" />
          </div>

          <BodyPartFilterPills
            value={bodyPart}
            onChange={setBodyPart}
            counts={bodyPartCounts}
            testIdPrefix="exercises-body-part"
          />

          {filtered.length === 0 ? (
            <div className="hs-card p-10 text-center">
              <BookOpen size={32} className="mx-auto text-ink-3" />
              <p className="mt-3 text-ink-2">{q ? "No matches." : "Your library is empty. Save your first exercise to start programming faster."}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((ex) => (
                <ExerciseCard
                  key={ex.id}
                  ex={ex}
                  onEdited={(u) => setItems((xs) => xs.map((x) => (x.id === u.id ? u : x)))}
                  onDeleted={(id) => setItems((xs) => xs.filter((x) => x.id !== id))}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="workouts" className="mt-6">
          <WorkoutsTab />
        </TabsContent>

        <TabsContent value="crossfit" className="mt-6">
          <CrossFitMovementsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Workouts (block templates) tab
// ────────────────────────────────────────────────────────────────

function WorkoutsTab() {
  const [items, setItems] = useState([]);
  const [wodOpen, setWodOpen] = useState(false);
  const [wods, setWods] = useState([]);
  const [wodQ, setWodQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/block-templates");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (wodOpen && wods.length === 0) {
      api.get("/wod-library").then((r) => setWods(r.data)).catch(() => {});
    }
  }, [wodOpen, wods.length]);

  const importWod = async (wod) => {
    setBusy(wod.slug);
    try {
      const { data } = await api.post(`/block-templates/from-wod/${wod.slug}`);
      setItems((xs) => [...xs, data].sort((a, b) => a.title.localeCompare(b.title)));
      toast.success(`${wod.name} saved to your library`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const del = async (id, title) => {
    if (!window.confirm(`Remove "${title}" from your workout library?`)) return;
    try {
      await api.delete(`/block-templates/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredWods = wods.filter((w) => !wodQ || w.name.toLowerCase().includes(wodQ.toLowerCase()));

  const fmtCap = (sec) => sec ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}` : null;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <p className="text-sm text-ink-2 max-w-xl">
          Save full conditioning workouts as templates — the rep scheme, time cap, and movements all come along. Insert any benchmark WOD with one tap, then tweak the weights for your client.
        </p>
        <Dialog open={wodOpen} onOpenChange={setWodOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="open-wod-import">
              <BookOpen size={16} className="mr-2" /> Insert from WOD library
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Save a benchmark to your library</DialogTitle>
              <DialogDescription>Tap any WOD to clone it. You can edit the title, notes, weights, and rep scheme afterwards.</DialogDescription>
            </DialogHeader>
            <Input
              placeholder="Search Fran, Cindy, Murph…"
              value={wodQ}
              onChange={(e) => setWodQ(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="wod-import-search"
            />
            <div className="mt-3 space-y-2 max-h-[50vh] overflow-y-auto">
              {filteredWods.map((w) => {
                const sc = w.scheme_config || {};
                const sub = [
                  sc.rep_scheme && sc.rep_scheme.join("-"),
                  sc.rounds && `${sc.rounds} rds`,
                  sc.time_cap_seconds && `cap ${fmtCap(sc.time_cap_seconds)}`,
                ].filter(Boolean).join(" · ");
                return (
                  <button
                    key={w.slug}
                    type="button"
                    onClick={() => importWod(w)}
                    disabled={busy === w.slug}
                    className="w-full text-left p-3 rounded-xl border border-line hover:border-terracotta hover:bg-terracotta/5 disabled:opacity-50"
                    data-testid={`import-wod-${w.slug}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold">{w.name}</div>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-ink-3 mt-0.5">{w.category} · {w.type} · {sub}</div>
                      </div>
                      <Plus size={16} className="text-terracotta mt-1 shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="hs-card p-10 text-center">
          <Lightning size={32} className="mx-auto text-ink-3" />
          <p className="mt-3 text-ink-2">No saved workouts yet. Click "Insert from WOD library" above to start with Fran or Murph.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((t) => (
            <BlockTemplateCard
              key={t.id}
              t={t}
              onEdited={(u) => setItems((xs) => xs.map((x) => (x.id === u.id ? u : x)))}
              onDelete={() => del(t.id, t.title)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BlockTemplateCard({ t, onEdited, onDelete }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState({
    title: t.title,
    notes: t.notes || "",
    rep_scheme: (t.scheme_config?.rep_scheme || []).join(", "),
    rounds: t.scheme_config?.rounds ?? "",
    time_cap_min: t.scheme_config?.time_cap_seconds != null ? +(t.scheme_config.time_cap_seconds / 60).toFixed(2) : "",
    movements: (t.movements || []).map((mv) => {
      const u = mv.weight_unit || userUnit;
      return { ...mv, weight: fromCanonicalLb(mv.weight, u), weight_unit: u };
    }),
  });
  const sc = t.scheme_config || {};
  const fmtCap = (sec) => sec ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}` : null;
  const sub = [
    sc.rep_scheme && sc.rep_scheme.join("-"),
    sc.rounds && `${sc.rounds} rds`,
    sc.time_cap_seconds && `cap ${fmtCap(sc.time_cap_seconds)}`,
  ].filter(Boolean).join(" · ");

  const updateMv = (idx, key, val) => {
    setDraft((d) => {
      const ms = [...d.movements];
      ms[idx] = { ...ms[idx], [key]: val };
      return { ...d, movements: ms };
    });
  };

  const save = async () => {
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const scheme = { ...sc };
      if (draft.rounds !== "") scheme.rounds = Number(draft.rounds);
      if (draft.time_cap_min !== "") scheme.time_cap_seconds = Math.round(Number(draft.time_cap_min) * 60);
      if (draft.rep_scheme) {
        const arr = draft.rep_scheme.split(",").map((s) => Number(s.trim())).filter((n) => !isNaN(n));
        if (arr.length) scheme.rep_scheme = arr;
      }
      const body = {
        title: draft.title,
        notes: draft.notes || null,
        scheme_config: scheme,
        movements: draft.movements.map((m) => ({
          ...m,
          weight: toCanonicalLb(m.weight, m.weight_unit),
          weight_unit: m.weight_unit || userUnit,
          reps: num(m.reps),
          calories: num(m.calories),
          time_seconds: num(m.time_seconds),
          distance_meters: num(m.distance_meters),
        })),
      };
      const { data } = await api.patch(`/block-templates/${t.id}`, body);
      toast.success("Saved");
      setEditOpen(false);
      onEdited(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hs-card p-5 animate-fade-up" data-testid={`block-template-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] bg-terracotta/10 text-terracotta">{t.type}</span>
            <h3 className="font-heading text-lg font-semibold truncate">{t.title}</h3>
          </div>
          {sub && <div className="text-[11px] uppercase tracking-[0.15em] text-ink-3 mt-0.5 font-data">{sub}</div>}
        </div>
        <div className="flex gap-1 shrink-0">
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button size="icon" variant="ghost" className="rounded-lg hover:bg-muted2" data-testid={`edit-template-${t.id}`}>
                <PencilSimple size={16} />
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Edit "{t.title}"</DialogTitle>
                <DialogDescription>Tweak the weights, rep scheme, or notes for your version.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="hs-label">Title</Label>
                  <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-2 h-11 rounded-xl" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="hs-label">Rounds</Label>
                    <Input type="number" min="1" value={draft.rounds} onChange={(e) => setDraft({ ...draft, rounds: e.target.value })} className="mt-2 h-11 rounded-xl font-data" />
                  </div>
                  <div className="col-span-2">
                    <Label className="hs-label">Rep scheme</Label>
                    <Input value={draft.rep_scheme} onChange={(e) => setDraft({ ...draft, rep_scheme: e.target.value })} placeholder="21, 15, 9" className="mt-2 h-11 rounded-xl font-data" />
                  </div>
                </div>
                <div>
                  <Label className="hs-label">Time cap (min)</Label>
                  <Input type="number" step="0.5" value={draft.time_cap_min} onChange={(e) => setDraft({ ...draft, time_cap_min: e.target.value })} className="mt-2 h-11 rounded-xl font-data" />
                </div>
                <div>
                  <Label className="hs-label">Movements</Label>
                  <div className="mt-2 space-y-2">
                    {draft.movements.map((m, i) => (
                      <div key={m.id || i} className="grid grid-cols-12 gap-2">
                        <Input value={m.name || ""} onChange={(e) => updateMv(i, "name", e.target.value)} className="col-span-4 h-10 rounded-lg" />
                        <div className="col-span-4 flex items-center gap-1 bg-muted2 rounded-lg pl-2 pr-1 h-10">
                          <Input
                            placeholder="Weight"
                            type="number"
                            step={(m.weight_unit || userUnit) === "level" ? "1" : "0.5"}
                            min={(m.weight_unit || userUnit) === "level" ? "0" : undefined}
                            value={m.weight ?? ""}
                            onChange={(e) => updateMv(i, "weight", e.target.value)}
                            className="h-8 rounded font-data flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
                          />
                          <div className="inline-flex bg-surface rounded p-0.5">
                            {["lb", "kg", "level"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => {
                                  const prev = m.weight_unit || userUnit;
                                  // Cross-domain switch → clear the number.
                                  if ((opt === "level" || prev === "level") && opt !== prev) {
                                    updateMv(i, "weight", "");
                                  }
                                  updateMv(i, "weight_unit", opt);
                                }}
                                className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.15em] ${
                                  (m.weight_unit || userUnit) === opt ? "bg-moss text-white" : "text-ink-3"
                                }`}
                                title={opt === "level" ? "Machine level" : opt}
                              >
                                {opt === "level" ? "L" : opt}
                              </button>
                            ))}
                          </div>
                        </div>
                        <Input placeholder="Reps" value={m.reps ?? ""} onChange={(e) => updateMv(i, "reps", e.target.value)} className="col-span-2 h-10 rounded-lg font-data" />
                        <Input placeholder="Cal" value={m.calories ?? ""} onChange={(e) => updateMv(i, "calories", e.target.value)} className="col-span-2 h-10 rounded-lg font-data" />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="hs-label">Notes</Label>
                  <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-2 rounded-xl" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setEditOpen(false)} className="rounded-xl">Cancel</Button>
                  <Button type="button" onClick={save} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid={`save-template-${t.id}`}>Save</Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
          <Button size="icon" variant="ghost" onClick={onDelete} className="rounded-lg text-error hover:bg-red-50 hover:text-error" data-testid={`delete-template-${t.id}`}>
            <Trash size={16} />
          </Button>
        </div>
      </div>
      {t.notes && <p className="mt-3 text-sm text-ink-2 line-clamp-2">{t.notes}</p>}
      <div className="mt-3 flex flex-wrap gap-1.5 font-data text-[11px]">
        {(t.movements || []).map((m, idx) => (
          <span key={m.id || idx} className="px-2 py-0.5 rounded-md bg-muted2 text-ink-2">
            {m.name}
            {m.weight ? ` @ ${formatStoredWeight(m.weight, m.weight_unit || userUnit)}` : ""}
            {m.reps ? ` ×${m.reps}` : ""}
            {m.distance_meters ? ` ${formatStoredDistance(m.distance_meters, m.distance_unit || "m")}` : ""}
            {m.height_inches ? ` ↕${formatStoredHeight(m.height_inches, m.height_unit || "in")}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// CrossFit movements tab (read-only reference from wod_library.py)
// ────────────────────────────────────────────────────────────────

const CATEGORY_LABEL = {
  foundational: "Foundational",
  squat: "Squat",
  deadlift: "Deadlift",
  press: "Press",
  olympic: "Olympic",
  powerlifting: "Powerlifting",
  gymnastics: "Gymnastics",
  core: "Core",
  plyometric: "Plyometric",
  kettlebell: "Kettlebell",
  conditioning: "Conditioning",
  strongman: "Strongman",
  accessory: "Accessory",
};

const CATEGORY_COLOR = {
  foundational: "bg-moss/10 text-moss",
  squat:        "bg-terracotta/10 text-terracotta",
  deadlift:     "bg-terracotta/10 text-terracotta",
  press:        "bg-terracotta/10 text-terracotta",
  olympic:      "bg-amber-100 text-amber-800",
  powerlifting: "bg-amber-100 text-amber-800",
  gymnastics:   "bg-sky-100 text-sky-800",
  core:         "bg-violet-100 text-violet-800",
  plyometric:   "bg-rose-100 text-rose-800",
  kettlebell:   "bg-lime-100 text-lime-800",
  conditioning: "bg-orange-100 text-orange-800",
  strongman:    "bg-stone-200 text-stone-800",
  accessory:    "bg-muted2 text-ink-2",
};

function CrossFitMovementsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [bodyPart, setBodyPart] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/movement-library")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const categories = Array.from(new Set(items.map((m) => m.category))).sort();
  const filtered = items.filter((m) => {
    if (cat !== "all" && m.category !== cat) return false;
    if (bodyPart && (m.body_part || "") !== bodyPart) return false;
    if (q && !m.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const bodyPartCounts = items.reduce((acc, m) => {
    const k = m.body_part;
    if (k) acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <p className="text-sm text-ink-2 max-w-2xl">
        Reference library of standard movements from the CrossFit.com training guide. Browse form-check cues, search for a lift, then head to your week view to program it.
      </p>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
        <Input
          placeholder="Search movements (e.g. Snatch, Muscle-Up, Thruster)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 h-12 rounded-xl"
          data-testid="crossfit-movements-search"
        />
      </div>

      <BodyPartFilterPills
        value={bodyPart}
        onChange={setBodyPart}
        counts={bodyPartCounts}
        testIdPrefix="crossfit-body-part"
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCat("all")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
            cat === "all" ? "bg-moss text-white" : "bg-muted2 text-ink-2 hover:bg-sage hover:text-moss"
          }`}
          data-testid="crossfit-cat-all"
        >
          All · {items.length}
        </button>
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCat(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
              cat === c ? "bg-moss text-white" : "bg-muted2 text-ink-2 hover:bg-sage hover:text-moss"
            }`}
            data-testid={`crossfit-cat-${c}`}
          >
            {CATEGORY_LABEL[c] || c} · {items.filter((m) => m.category === c).length}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="hs-card p-10 text-center text-ink-3">Loading CrossFit library…</div>
      ) : filtered.length === 0 ? (
        <div className="hs-card p-10 text-center">
          <BarbellIcon size={32} className="mx-auto text-ink-3" />
          <p className="mt-3 text-ink-2">No movements match your search.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="crossfit-movements-grid">
          {filtered.map((m) => (
            <div
              key={m.name}
              className="hs-card p-4 animate-fade-up"
              data-testid={`crossfit-movement-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-base font-semibold leading-tight">{m.name}</h3>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] shrink-0 ${CATEGORY_COLOR[m.category] || "bg-muted2 text-ink-2"}`}>
                  {CATEGORY_LABEL[m.category] || m.category}
                </span>
              </div>
              {(m.body_part || m.primary_muscle) && (
                <div className="mt-2">
                  <BodyPartChip bodyPart={m.body_part} primaryMuscle={m.primary_muscle} />
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5 font-data text-[11px]">
                {m.default_reps != null && (
                  <span className="px-2 py-0.5 rounded-md bg-muted2 text-ink-2">{m.default_reps} reps</span>
                )}
                {m.default_weight != null && (
                  <span className="px-2 py-0.5 rounded-md bg-sage text-moss font-semibold">{m.default_weight} lb</span>
                )}
                {m.default_time_seconds != null && (
                  <span className="px-2 py-0.5 rounded-md bg-muted2 text-ink-2">{formatDuration(m.default_time_seconds)}</span>
                )}
                {m.default_distance_meters != null && (
                  <span className="px-2 py-0.5 rounded-md bg-muted2 text-ink-2">{m.default_distance_meters} m</span>
                )}
                {m.default_calories != null && (
                  <span className="px-2 py-0.5 rounded-md bg-terracotta/10 text-terracotta font-semibold inline-flex items-center gap-1">
                    <Fire size={10} weight="fill" /> {m.default_calories} cal
                  </span>
                )}
              </div>
              {m.form_notes && (
                <div className="mt-3 p-2.5 rounded-lg bg-sage/50 border border-sage">
                  <div className="flex items-center gap-1.5 hs-label text-[10px] text-moss">
                    <Shield size={11} weight="bold" /> Form check
                  </div>
                  <p className="mt-1 text-[13px] text-moss-hover leading-relaxed">{m.form_notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
